/*
 * Copyright (C) 2024 S&P Global.
 * All Rights Reserved
 */
import { AppIdentifier, AppIntent, AppMetadata, Channel, Context, ContextHandler, ContextMetadata, ContextType, DesktopAgent, ImplementationMetadata, Intent, IntentHandler, IntentResolution, IntentResult, Listener, OpenError, PrivateChannel, ResolveError } from "@finos/fdc3";
import { agentProviderNamePfdc3, fdc3Version, spgiFusionInteropVersion } from "fusion-interop-common/src";
import { InteropService } from "fusion-interop-common/src/messaging";
import { AppRefManager } from "fusion-interop-common/src/lifecycle";
import { AppDefinition } from "fusion-interop-common/src/types/app-directory";
import { Fdc3IntentListener, FusionInteropMessage, MessageType, SystemMessageType } from "fusion-interop-common/src/types/message";
import { Fdc3ApiMethodRequestPayload, Fdc3ApiMethodResponsePayload } from "fusion-interop-common/src/types/message/payloads";
import { RoutePattern } from "fusion-interop-common/src/types/routing";
import { RaiseIntentRequest, RaiseIntentResponse } from "fusion-interop-common/src/types/message/fdc3-for-web/BrowserTypes";

export class Pfdc3Agent implements DesktopAgent {
  private _browserGlobalScope: Window & typeof globalThis;
  private _appIdentifier: AppIdentifier;
  private _interopService: InteropService;
  private _appRefManager: AppRefManager;
  private _appDef: AppDefinition;
  private _intentListeners: Fdc3IntentListener[] = [];

  // prettier-ignore
  constructor(browserGlobalScope: Window & typeof globalThis,
              appIdentifier: AppIdentifier,
              interopService: InteropService,
              appRefManager: AppRefManager,
              appDef: AppDefinition) {
    console.log("Pfdc3Agent.constructor");
    if (!browserGlobalScope) {
      throw new Error("browserGlobalScope not provided");
    }
    if (!interopService) {
      throw new Error("interopService not provided");
    }
    if (!appRefManager) {
      throw new Error("appRefManager not provided");
    }
    if (!appDef) {
      throw new Error("appDef not provided");
    }
    this._browserGlobalScope = browserGlobalScope;
    this._appIdentifier = appIdentifier;
    this._interopService = interopService;
    this._appRefManager = appRefManager;
    this._appDef = appDef;
    this._createRaiseIntentRequestRequestRouteHandler();
  }

  public async open(app: AppIdentifier, context?: Context): Promise<AppIdentifier>;
  public async open(name: string, context?: Context): Promise<AppIdentifier>;
  public async open(appOrName: AppIdentifier | string, context?: Context): Promise<AppIdentifier> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async findIntent(intent: Intent, context?: Context, resultType?: string): Promise<AppIntent> {
    console.log("Pfdc3Agent.findIntent");
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async findIntentsByContext(context: Context, resultType?: string): Promise<Array<AppIntent>> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async findInstances(app: AppIdentifier): Promise<Array<AppIdentifier>> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async broadcast(context: Context): Promise<void> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async raiseIntent(intent: Intent, context: Context, app?: AppIdentifier): Promise<IntentResolution>;
  public async raiseIntent(intent: Intent, context: Context, name: string): Promise<IntentResolution>;
  public async raiseIntent(intent: string, context: Context, appOrName?: AppIdentifier | string): Promise<IntentResolution> {
    console.log("Pfdc3Agent.raiseIntent");
    //TODO - Validate context and throw ResolveError.MalformedContext error if necessary
    const app: AppIdentifier | undefined = this._deriveAppIdentifier(appOrName);
    if (!app) {
      throw new Error(ResolveError.NoAppsFound);
    }

    // Create fdc3ApiMethodRequest payload
    const reqPayload: Fdc3ApiMethodRequestPayload = {
      type: SystemMessageType.fdc3ApiMethodRequest,
      fdc3Message: <RaiseIntentRequest>{
        type: "raiseIntentRequest",
        payload: {
          app,
          context,
          intent
        }
      }
    };
    // Get target app reference
    //TODO - This is clumsy having to lookup the appTypes for the apps to pass to interopService.request(), so look at refactoring InteropService
    //TODO - In a full FDC3 Desktop Agent this is where an Intent Resolver UI would be used in the case where an appId was specified without an instanceId. Consider further what else we could do here in this more limited Peer-to-Peer FDC3 DA scenario.
    const targetAppRef = this._appRefManager.getFirstAppReference(app);
    if (!targetAppRef) {
      throw new Error(app.instanceId ? ResolveError.TargetInstanceUnavailable : ResolveError.TargetAppUnavailable);
    }
    // Invoke fdc3ApiMethodRequest for raiseIntentRequest
    const resVerPayload = await this._interopService.request<Fdc3ApiMethodResponsePayload>(reqPayload, targetAppRef.appType, app);
    // console.log("resVerPayload");
    // console.log(resVerPayload);
    const raiseIntentResponse = resVerPayload.payload.fdc3Message as RaiseIntentResponse;
    raiseIntentResponse.payload.appIntent
    if (!raiseIntentResponse.payload.intentResolution?.source) {
      throw new Error(ResolveError.IntentDeliveryFailed);
    }

    // Return intent resolution
    const intentResult: Context | Channel | void = undefined;
    const intentResolution: IntentResolution = {
      source: raiseIntentResponse.payload.intentResolution.source,
      intent,
      getResult: (): Promise<IntentResult> => {
        return Promise.resolve(intentResult);
      },
    };
    return Promise.resolve(intentResolution);
  }

  public async raiseIntentForContext(context: Context, app?: AppIdentifier): Promise<IntentResolution>;
  public async raiseIntentForContext(context: Context, name: string): Promise<IntentResolution>;
  public async raiseIntentForContext(context: Context, appOrName?: AppIdentifier | string): Promise<IntentResolution> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async addIntentListener(intent: Intent, handler: IntentHandler): Promise<Listener> {
    console.log("Pfdc3Agent.addIntentListener");
    if (!intent) {
      throw new Error("intent not provided");
    }
    if (!handler) {
      throw new Error("handler not provided");
    }
    if (typeof handler !== "function") {
      throw new Error("handler is not a function");
    }
    const intentListener: Fdc3IntentListener = {
      intent,
      handler,
    };
    this._intentListeners.push(intentListener);

    const listenerResponse: Listener = {
      unsubscribe: () => {
        console.log("addIntentListener unsubscribe");
        const intentIndex: number = this._intentListeners.findIndex((listener: Fdc3IntentListener) => listener === intentListener);
        if (intentIndex !== -1) {
          this._intentListeners.splice(intentIndex, 1);
        }
      },
    };
    return Promise.resolve(listenerResponse);
  }

  public async addContextListener(contextType: ContextType | null, handler: ContextHandler): Promise<Listener>;
  public async addContextListener(handler: ContextHandler): Promise<Listener>;
  public async addContextListener(contextTypeOrHandler: ContextType | null | ContextHandler, handler?: ContextHandler): Promise<Listener> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async getUserChannels(): Promise<Array<Channel>> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async joinUserChannel(channelId: string): Promise<void> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async getOrCreateChannel(channelId: string): Promise<Channel> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async createPrivateChannel(): Promise<PrivateChannel> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async getCurrentChannel(): Promise<Channel | null> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async leaveCurrentChannel(): Promise<void> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async getInfo(): Promise<ImplementationMetadata> {
    console.log("Pfdc3Agent.getInfo");
    const implementationMetadata: ImplementationMetadata = {
      fdc3Version: fdc3Version,
      provider: agentProviderNamePfdc3,
      providerVersion: spgiFusionInteropVersion,
      optionalFeatures: {
        OriginatingAppMetadata: false,
        UserChannelMembershipAPIs: false,
        DesktopAgentBridging: false,
      },
      appMetadata: {
        appId: this._appIdentifier.appId,
        instanceId: this._appIdentifier.instanceId,
      },
    };
    return Promise.resolve(implementationMetadata);
  }

  public async getAppMetadata(app: AppIdentifier): Promise<AppMetadata> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async getSystemChannels(): Promise<Array<Channel>> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  public async joinChannel(channelId: string): Promise<void> {
    throw new Error("Method not implemented by Pfdc3Agent.");
  }

  private _createRaiseIntentRequestRequestRouteHandler(): void {
    const routePattern: RoutePattern = {
      type: MessageType.spgiFusionSystem,
      systemMessageType: SystemMessageType.fdc3ApiMethodRequest,
    };
    this._interopService.addRouteHandler(routePattern, async (requestMessage: FusionInteropMessage<Fdc3ApiMethodRequestPayload>, requestOrigin: string, requestSourceWindow: WindowProxy): Promise<void> => {
      //TODO - Validate requestOrigin matches the origin in the AppReference for the app identifier in the header of the requestMessage
      // Invoke relevant intent handlers
      if (requestMessage.payload.fdc3Message.type == "raiseIntentRequest") {
        const raiseIntentRequest = requestMessage.payload.fdc3Message as RaiseIntentRequest;
        // prettier-ignore
        const relevantHandlers = this._intentListeners
                                  .filter((il) => il.intent === raiseIntentRequest.payload.intent)
                                  .map((il) => il.handler);
        for (const handler of relevantHandlers) {
          await handler(raiseIntentRequest.payload.context, raiseIntentRequest.meta as ContextMetadata);
        }
        // Respond to raiseIntentRequest
        //TODO - Read FDC3 spec to see what scenarios should result in an error being returned in the raiseIntentResponse
        const responsePayload: Fdc3ApiMethodResponsePayload = {
          type: SystemMessageType.fdc3ApiMethodResponse,
          fdc3Message: <RaiseIntentResponse>{
            type: "raiseIntentResponse",
            payload: {
              error: undefined,
              intentResolution: {
                intent: raiseIntentRequest.payload.intent,
                source: this._appIdentifier,
              }
            }
          }
        };
        this._interopService.respond(responsePayload, requestMessage);
      }
    });
  }

  private _deriveAppIdentifier(appOrName?: AppIdentifier | string): AppIdentifier | undefined {
    let app: AppIdentifier | undefined;
    if (appOrName && typeof appOrName === "object" && appOrName.appId) {
      app = appOrName as AppIdentifier;
    }
    else if (typeof appOrName === "string") {
      app = {
        appId: appOrName,
      };
    }
    return app;
  }
}
